import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Flame, Crown, Swords, Play, FastForward, Trophy } from "lucide-react";

const countdownStr = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "resolving now";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const CrowdWall = ({ crowd, meId }) => (
  <div data-testid="arena-crowd-wall">
    <div className="text-[9px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-2">
      The Gathering — {crowd.length} warrior{crowd.length === 1 ? "" : "s"} in the arena
    </div>
    <div className="flex flex-wrap gap-2">
      {crowd.map((p) => (
        <div key={p.user_id} title={`${p.name} — ${p.stake} BLC in the pot`}
          className={`flex items-center gap-1.5 border px-2 py-1 bg-black/60 ${p.user_id === meId ? "border-yellow-400" : "border-pb-border"}`}>
          {p.avatar
            ? <img src={p.avatar} alt={p.name} className="w-6 h-6 rounded-full object-cover" />
            : <span className="w-6 h-6 rounded-full bg-pb-elevated flex items-center justify-center text-[9px] font-black text-zinc-400">{(p.name || "?")[0]}</span>}
          <span className="text-[9px] font-bold truncate max-w-[80px]">{p.name}</span>
          <span className="text-[8px] font-mono text-yellow-400">{p.stake}</span>
        </div>
      ))}
      {crowd.length === 0 && <span className="text-xs text-zinc-600">The arena is empty… be the first through the gates.</span>}
    </div>
  </div>
);

function MatchTheater({ match, label, onDone }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    if (match.bye) { const t = setTimeout(onDone, 1400); return () => clearTimeout(t); }
    const iv = setInterval(() => setShown((s) => {
      if (s >= match.rounds.length) { clearInterval(iv); setTimeout(onDone, 1800); return s; }
      return s + 1;
    }), 650);
    return () => clearInterval(iv);
  }, [match]);
  if (match.bye) {
    return (
      <div className="text-center py-8 animate-fade-up" data-testid="arena-theater-bye">
        <div className="text-[9px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-2">{label}</div>
        <div className="font-display font-black text-xl">{match.p1.name}</div>
        <div className="text-xs text-zinc-400 mt-1">advances on a bye — the crowd waits…</div>
      </div>
    );
  }
  const done = shown >= match.rounds.length;
  const w = match.winner_id === match.p1.user_id ? "p1" : "p2";
  return (
    <div className="animate-fade-up" data-testid="arena-theater-match">
      <div className="text-center text-[9px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-3">{label}</div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 mb-4">
        <div className={`text-right ${done && w === "p1" ? "text-yellow-400" : ""}`}>
          <div className="font-display font-black text-lg leading-tight">{match.p1.name}</div>
          <div className="font-mono text-2xl font-bold">{done ? match.s1 : match.rounds.slice(0, shown).reduce((a, r) => a + r.a.score, 0).toFixed(1)}</div>
        </div>
        <Swords className={`w-6 h-6 ${done ? "text-yellow-400" : "text-pb-red animate-pulse"}`} />
        <div className={done && w === "p2" ? "text-yellow-400" : ""}>
          <div className="font-display font-black text-lg leading-tight">{match.p2.name}</div>
          <div className="font-mono text-2xl font-bold">{done ? match.s2 : match.rounds.slice(0, shown).reduce((a, r) => a + r.b.score, 0).toFixed(1)}</div>
        </div>
      </div>
      <div className="space-y-1.5">
        {match.rounds.slice(0, shown).map((r) => (
          <div key={r.round} className={`grid grid-cols-[1fr_auto_1fr] items-center gap-2 border px-2 py-1.5 animate-fade-up ${r.clutch ? "border-yellow-400/50 bg-yellow-400/5" : "border-pb-border bg-black/40"}`}>
            <div className={`text-right text-[10px] truncate ${r.a.score > r.b.score ? "text-white font-bold" : "text-zinc-500"}`}>
              {r.a.card} <span className="font-mono">{r.a.score}</span>
            </div>
            <div className="text-[7px] font-black uppercase tracking-widest text-zinc-500 text-center w-16">
              R{r.round} · {r.stat}{r.clutch ? " ⚡" : ""}
            </div>
            <div className={`text-[10px] truncate ${r.b.score > r.a.score ? "text-white font-bold" : "text-zinc-500"}`}>
              <span className="font-mono">{r.b.score}</span> {r.b.card}
            </div>
          </div>
        ))}
      </div>
      {done && (
        <div className="text-center mt-3 text-yellow-400 font-black uppercase tracking-widest text-sm animate-fade-up" data-testid="arena-match-winner">
          🏟 {w === "p1" ? match.p1.name : match.p2.name} advances!
        </div>
      )}
    </div>
  );
}

const BracketTree = ({ bracket, meId, winnerId }) => (
  <div className="overflow-x-auto pb-2" data-testid="arena-bracket-tree">
    <div className="flex gap-4 min-w-max">
      {bracket.map((rnd, i) => (
        <div key={i} className="flex flex-col justify-around gap-2 min-w-[180px]">
          <div className="text-[8px] font-black uppercase tracking-[0.25em] text-zinc-500 text-center">{rnd.label}</div>
          {rnd.matches.map((m, j) => (
            <div key={j} className="border border-pb-border bg-black/50 text-[10px]">
              {[["p1", "s1"], ["p2", "s2"]].map(([pk, sk]) => {
                const p = m[pk];
                if (!p) return <div key={pk} className="px-2 py-1 text-zinc-600 italic">bye</div>;
                const isW = m.winner_id === p.user_id;
                return (
                  <div key={pk} className={`flex justify-between gap-2 px-2 py-1 ${isW ? "text-yellow-400 font-bold" : "text-zinc-400"} ${p.user_id === meId ? "bg-yellow-400/10" : ""}`}>
                    <span className="truncate">{p.user_id === winnerId && isW && i === bracket.length - 1 ? "👑 " : ""}{p.name}</span>
                    <span className="font-mono">{m.bye ? "—" : m[sk]}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export default function BullpenArena({ meId, onClose }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("lobby");
  const [cursor, setCursor] = useState([0, 0]);
  const flat = useMemo(() => {
    const b = data?.last?.bracket || [];
    return b.flatMap((rnd, i) => rnd.matches.map((m, j) => ({ m, label: rnd.label, i, j })));
  }, [data]);
  const idxRef = useRef(0);
  useEffect(() => { api.get("/cardwars/event/arena").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  const { open, last } = data;

  const startShow = () => { idxRef.current = 0; setCursor([0, 0]); setMode("show"); };
  const nextMatch = () => {
    idxRef.current += 1;
    if (idxRef.current >= flat.length) setMode("champion");
    else setCursor([flat[idxRef.current].i, flat[idxRef.current].j]);
  };
  const current = flat[idxRef.current];

  return (
    <div className="fixed inset-0 z-[90] bg-black/95 overflow-y-auto" data-testid="bullpen-arena">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-black uppercase text-xl text-pb-red flex items-center gap-2">
            <Flame className="w-5 h-5" /> The Bullpen Arena
          </div>
          <button data-testid="arena-close" onClick={onClose}><X className="w-6 h-6 text-zinc-500 hover:text-white" /></button>
        </div>

        {/* Live lobby */}
        <div className="border border-pb-red/40 bg-gradient-to-br from-red-950/30 to-black p-5 mb-6">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div>
              <div className="text-[8px] font-black uppercase tracking-[0.3em] text-zinc-500">This Week's Pot</div>
              <div className="font-display font-black text-3xl text-yellow-400" data-testid="arena-pot">{open.pot} <span className="text-base">BLC</span></div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              Gates close in <b className="text-white">{countdownStr(open.resolves_at)}</b><br />
              <span className="text-zinc-600">bracket resolves · winner takes all</span>
            </div>
            {open.entered && <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-[#A3E635] border border-[#A3E635]/60 px-2.5 py-1">You're in the arena</span>}
          </div>
          <CrowdWall crowd={open.crowd} meId={meId} />
        </div>

        {/* Last battle replay */}
        {last ? (
          <div className="border border-pb-border bg-pb-surface p-5">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="font-display font-black uppercase text-sm flex items-center gap-2">
                <Trophy className="w-4 h-4 text-yellow-400" /> Last Battle — {last.week_key}
              </span>
              <span className="text-[10px] text-zinc-500">{last.entrants} fought · <b className="text-yellow-400">{last.winner_name}</b> took {last.pot_paid} BLC</span>
              {mode === "lobby" && flat.length > 0 && (
                <button data-testid="arena-play-show" onClick={startShow}
                  className="ml-auto flex items-center gap-1.5 bg-pb-red text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 hover:bg-red-500 transition-colors">
                  <Play className="w-3.5 h-3.5" /> Play the Show
                </button>
              )}
              {mode === "show" && (
                <button data-testid="arena-skip-show" onClick={() => setMode("champion")}
                  className="ml-auto flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:text-white transition-colors">
                  <FastForward className="w-3.5 h-3.5" /> Skip to Champion
                </button>
              )}
            </div>
            {mode === "show" && current && (
              <div className="border border-pb-red/30 bg-black/60 p-4 mb-4">
                <MatchTheater key={`${cursor[0]}-${cursor[1]}`} match={current.m} label={current.label} onDone={nextMatch} />
              </div>
            )}
            {mode === "champion" && (
              <div className="text-center border border-yellow-400/60 bg-yellow-400/5 py-8 mb-4 animate-fade-up" data-testid="arena-champion">
                <Crown className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
                <div className="text-[9px] font-black uppercase tracking-[0.4em] text-zinc-500">Arena Champion</div>
                <div className="font-display font-black text-3xl text-yellow-400 my-1">{last.winner_name}</div>
                <div className="text-sm text-zinc-300">walked out with the entire <b className="text-yellow-400">{last.pot_paid} BLC</b> pot</div>
              </div>
            )}
            <BracketTree bracket={last.bracket} meId={meId} winnerId={last.winner_id} />
          </div>
        ) : (
          <div className="border border-pb-border bg-pb-surface p-6 text-center text-sm text-zinc-500">
            No battle has been fought yet — the first bracket resolves Friday night.
          </div>
        )}
      </div>
    </div>
  );
}
